import { useState, useEffect, useRef, FormEvent } from 'react';
import { useIntl } from 'react-intl';
import { ContentLayout, FormField, Input } from '@amzn/awsui-components-react';

import TopHeader from '../components/TopHeader';
import Main from '../components/Main';
import Footer from '../components/Footer';
import GandalfFlashbar from '../components/GandalfFlashbar';
import ChooseSignInMethod from '../components/ChooseSignInMethod';
import usePageTitle from '../hooks/usePageTitle';
import { useSignInPageSetup } from '../hooks/useSignInPageSetup';
import { AuthFactorHintValues, IDP_URL_PARAM } from '../constants/auth';
import {
  IDP_ESSO,
  IdPs,
  isEltiProvider,
  isStandardProvider,
} from '../constants/providers';
import useLoginMethod from '../hooks/useLoginMethod';
import useProviders from '../hooks/useProviders';
import metricsService from '../services/metrics';
import {
  GANDALF_SESSION,
  IMPRESSIONS,
  SimplifiedLoginRumEvent,
} from '../constants/metrics';
import { AppConfig } from '../types/app';
import messages from '../i18n/simplifiedSignIn.messages';
import styles from './SimplifiedSignIn.module.css';
import googleLogo from '../assets/google-logo.svg';
import appleLogo from '../assets/apple-logo.svg';
import githubLogo from '../assets/github-logo.svg';
import amazonLogo from '../assets/lwa.svg';
import keyIcon from '../assets/key.svg';
import infoIcon from '../assets/status-info.svg';
import { validateEmail } from '../utils/email';
import { isApnDeprecated } from '../utils/apnDeprecationHelper';
import Cookies from 'js-cookie';
import { VERIFY_EMAIL_COOKIE_NAME } from '../components/postAuth/VerifyEmail';
import providersService from '../services/providers';
import { rumService } from '../services/rum';
import { AppURL } from '../constants/urls';
import { LAST_USED_PROVIDER_KEY } from '../constants/storage';
import { getSessionState } from '../hooks/useSignIn';
import { queryApi } from '../hooks/useGetPublicIdpDetails';
import logger from '../utils/logger';

function SimplifiedSignIn({ config }: { config: AppConfig }) {
  usePageTitle();
  const { formatMessage } = useIntl();
  const { enableCookieComponent, handleRedirectToIdp } =
    useSignInPageSetup(config);
  const metricsPublisher = useRef(
    metricsService.getPublisher('SimplifiedSignInPage')
  );
  const {
    isLoading,
    error: loginMethodError,
    loginMethods,
    fetchLoginMethod,
    resetLoginMethods,
  } = useLoginMethod(config);

  // Handle identity_provider URL param (e.g. from replayAuth retry)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const preferredIdp = urlParams.get(IDP_URL_PARAM);
    if (preferredIdp && preferredIdp.toLowerCase() !== 'cognito') {
      const provider = providersService.transformNameToIdProvider(
        preferredIdp,
        config.gandalfDomain
      );
      metricsPublisher.current.publishCounterMonitor('PreferredIdpRedirect', 1);
      if (preferredIdp === IdPs.OTP) {
        sessionStorage.setItem('otp_prefill_email', '');
      }
      handleRedirectToIdp(provider);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [helpExpanded, setHelpExpanded] = useState(false);
  const [showChooseMethod, setShowChooseMethod] = useState(false);
  const [checkingSession, setCheckingSession] = useState(() => {
    if (config.enableAuthSessionReuse !== 'true') return false;
    try {
      const raw = localStorage.getItem(LAST_USED_PROVIDER_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      if (!parsed?.idp || !parsed?.createdAt) return false;
      if (providersService.isCreatedAtTooOld(parsed.createdAt)) return false;
      return true;
    } catch {
      return false;
    }
  });

  // Session reuse: if there's an active session, auto-redirect (SSO)
  useEffect(() => {
    if (!checkingSession) return;

    const raw = localStorage.getItem(LAST_USED_PROVIDER_KEY);
    if (!raw) {
      setCheckingSession(false);
      return;
    }
    let lastUsed;
    try {
      lastUsed = JSON.parse(raw);
    } catch {
      setCheckingSession(false);
      return;
    }
    if (!lastUsed?.idp) {
      setCheckingSession(false);
      return;
    }
    const storedIdp = lastUsed.idp;

    // Fetch full IDP details (including idpType) before session check
    queryApi(config, storedIdp.idp)
      .then((idpDetails) => {
        if (!idpDetails?.authEndpoint) {
          setCheckingSession(false);
          return;
        }

        const idp = {
          ...storedIdp,
          url: providersService.getIdpUrl(
            storedIdp.idp,
            idpDetails.authEndpoint
          ),
          userPoolId: idpDetails.userPoolId,
          clientId: idpDetails.clientId,
          idpType: idpDetails.idpType,
        };

        // ELTI providers are not eligible for session reuse
        if (isEltiProvider(idp.idpType)) {
          setCheckingSession(false);
          return;
        }

        // Deprecated APN provider is not eligible for session reuse
        if (isApnDeprecated(config) && idp.idp === IdPs.AWSPartnerNetwork) {
          setCheckingSession(false);
          return;
        }

        return getSessionState(config.sessionApiPath).then(
          (hasActiveSession: boolean) => {
            if (hasActiveSession) {
              logger.debug(
                'SimplifiedSignIn: Active session found, redirecting.'
              );
              metricsPublisher.current.publishCounterMonitor(
                'SSO:SessionStateActive',
                1
              );

              if (
                config.enableSkipIDPAuthInGandalfSession === 'true' &&
                config.enableGandalfSession === 'true' &&
                !isStandardProvider(idp.idp) &&
                idp.idpType === IDP_ESSO
              ) {
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.set(IDP_URL_PARAM, GANDALF_SESSION);
                handleRedirectToIdp({
                  idp: GANDALF_SESSION,
                  userPoolId: config.authUserPoolId,
                  url: `https://${
                    config.gandalfDomain
                  }/oauth2/authorize?${urlParams.toString()}`,
                  clientId: config.authClientId,
                });
              } else {
                handleRedirectToIdp(idp);
              }
            } else {
              metricsPublisher.current.publishCounterMonitor(
                'SSO:SessionStateInactive',
                1
              );
              setCheckingSession(false);
            }
          }
        );
      })
      .catch(() => {
        metricsPublisher.current.publishCounterMonitor(
          'SSO:SessionStateError',
          1
        );
        setCheckingSession(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { providers: appClientProviders } = useProviders(config);
  const builderIdEnabled =
    !appClientProviders ||
    appClientProviders.some((p) => p.idp === IdPs.AWSBuilderID);

  const enableGoogle = config.enableGoogleSignIn !== 'false';
  const enableApple = config.enableAppleSignIn !== 'false';
  const enableGithub = config.enableGithubSignIn !== 'false';
  const enableAmazon = config.enableAmazonSignIn !== 'false';
  const hasSocialProviders =
    builderIdEnabled &&
    (enableGoogle || enableApple || enableGithub || enableAmazon);

  // When loginMethod API returns exactly 1 IDP, redirect to it (SSO flow)
  // When more than 1 IDP, show Choose Sign In Method inline
  useEffect(() => {
    if (loginMethods?.length === 1) {
      const idpName = loginMethods[0];
      const provider = providersService.transformNameToIdProvider(
        idpName,
        config.gandalfDomain
      );
      metricsPublisher.current.publishCounterMonitor('SSORedirect', 1);
      rumService.recordEvent('SimplifiedSignInSSORedirect', {
        provider: idpName,
        url: window.location.href,
        userAgent: navigator.userAgent,
        path: `${window.location.pathname}${window.location.search}`,
      });
      if (idpName === IdPs.OTP) {
        sessionStorage.setItem('otp_prefill_email', email);
      }
      handleRedirectToIdp(provider, email);
    } else if (loginMethods && loginMethods.length > 1) {
      setShowChooseMethod(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginMethods, config.gandalfDomain, handleRedirectToIdp]);

  // Capture metric when loginMethod API returns an error
  useEffect(() => {
    if (loginMethodError) {
      metricsPublisher.current.publishCounterMonitor('Error', 1);
      metricsPublisher.current.publishStringTruncate(
        'ErrorMsg',
        loginMethodError.toString()
      );
      metricsPublisher.current.publishStringTruncate(
        'UserAgent',
        navigator.userAgent
      );
      rumService.recordEvent('SimplifiedSignInError', {
        error: loginMethodError.message,
        url: window.location.href,
        userAgent: navigator.userAgent,
        path: `${window.location.pathname}${window.location.search}`,
      });
    }
  }, [loginMethodError]);

  useEffect(() => {
    metricsPublisher.current.publishCounterMonitor(IMPRESSIONS, 1);
    rumService.recordEvent(SimplifiedLoginRumEvent.PAGE_IMPRESSION, {
      loginHintEnabled: config.enableBIDLoginHintPrefill === 'true',
      url: window.location.href,
      path: `${window.location.pathname}${window.location.search}`,
    });
    Cookies.remove(VERIFY_EMAIL_COOKIE_NAME);
    const handlePageShow = (event: PageTransitionEvent) => {
      const isBFEventOld =
        window.performance &&
        window.performance.navigation.type ===
          window.performance.navigation.TYPE_BACK_FORWARD;
      const navEntry = performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;
      const isBFEventNew = navEntry?.type === 'back_forward';
      if (isBFEventOld || isBFEventNew || event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [config.enableBIDLoginHintPrefill]);

  const handleContinue = (e: FormEvent) => {
    e.preventDefault();
    const isValid = validateEmail(email);
    setIsValidEmail(isValid);
    if (!isValid) return;
    rumService.recordEvent(SimplifiedLoginRumEvent.EMAIL_SUBMITTED, {
      loginHintEnabled: config.enableBIDLoginHintPrefill === 'true',
      url: window.location.href,
    });
    fetchLoginMethod(email);
  };

  const handleSocialSignIn = (providerName: string) => {
    rumService.recordEvent(SimplifiedLoginRumEvent.SOCIAL_SIGN_IN_CLICKED, {
      provider: providerName,
      url: window.location.href,
    });
    const provider = providersService.transformNameToIdProvider(
      IdPs.AWSBuilderID,
      config.gandalfDomain
    );
    handleRedirectToIdp(provider, undefined, providerName);
  };

  if (checkingSession) {
    return (
      <div className={styles.page}>
        <div className={styles.stickyHeader}>
          <TopHeader config={config} />
        </div>
        <div className={styles.mainContent}>
          <Main config={config}>
            <ContentLayout defaultPadding maxContentWidth={1040} />
          </Main>
        </div>
        <Footer enableCookieComponent={enableCookieComponent} />
      </div>
    );
  }

  if (showChooseMethod && loginMethods) {
    return (
      <div className={styles.page}>
        <div className={styles.stickyHeader}>
          <TopHeader config={config} />
        </div>
        <div className={styles.mainContent}>
          <Main config={config}>
            <ContentLayout defaultPadding maxContentWidth={1040}>
              <GandalfFlashbar />
              <ChooseSignInMethod
                config={config}
                email={email}
                loginMethods={loginMethods}
                onChangeEmail={() => {
                  setShowChooseMethod(false);
                  resetLoginMethods();
                }}
                onRedirectToIdp={handleRedirectToIdp}
              />
            </ContentLayout>
          </Main>
        </div>
        <Footer enableCookieComponent={enableCookieComponent} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.stickyHeader}>
        <TopHeader config={config} />
      </div>
      <div className={styles.mainContent}>
        <Main config={config}>
          <ContentLayout defaultPadding maxContentWidth={1040}>
            <GandalfFlashbar />
            <div className={styles.container}>
              <div className={styles.card}>
                <h1 className={styles.header}>
                  {formatMessage(messages.header)}
                </h1>

                {loginMethodError && (
                  <div className={styles.errorBanner} role="alert">
                    <svg
                      className={styles.errorBannerIcon}
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle cx="8" cy="8" r="7.5" stroke="#d91515" />
                      <path
                        d="M8 4v5M8 11v1"
                        stroke="#d91515"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span>{formatMessage(messages.loginMethodError)}</span>
                  </div>
                )}

                <form onSubmit={handleContinue} noValidate>
                  <FormField
                    errorText={
                      !isValidEmail ? formatMessage(messages.emailInvalid) : ''
                    }
                  >
                    <span className={styles.inputLabel}>
                      {formatMessage(messages.emailLabel)}
                    </span>
                    <Input
                      type="email"
                      value={email}
                      ariaLabel={formatMessage(messages.emailLabel)}
                      placeholder={formatMessage(messages.emailPlaceholder)}
                      onChange={({ detail }) => {
                        setEmail(detail.value);
                        setIsValidEmail(true);
                      }}
                      data-testid="simplified-email-input"
                    />
                  </FormField>

                  <button
                    type="submit"
                    className={styles.primaryButton}
                    data-testid="simplified-continue-button"
                    disabled={isLoading}
                  >
                    {formatMessage(messages.continueButton)}
                  </button>
                </form>

                {/* Separator */}
                {hasSocialProviders && (
                  <div className={styles.separator}>
                    <div className={styles.separatorLine} />
                    <span className={styles.separatorText}>
                      {formatMessage(messages.orContinueWith)}
                    </span>
                    <div className={styles.separatorLine} />
                  </div>
                )}

                {/* Social sign-in tiles */}
                {hasSocialProviders && (
                  <div className={styles.socialTiles}>
                    {enableGoogle && (
                      <button
                        type="button"
                        className={styles.socialTile}
                        onClick={() =>
                          handleSocialSignIn(AuthFactorHintValues.GOOGLE)
                        }
                        data-testid="simplified-google-button"
                      >
                        <img
                          src={googleLogo}
                          alt=""
                          className={styles.socialIcon}
                        />
                        <span className={styles.socialLabel}>
                          {formatMessage(messages.googleLabel)}
                        </span>
                      </button>
                    )}
                    {enableApple && (
                      <button
                        type="button"
                        className={styles.socialTile}
                        onClick={() =>
                          handleSocialSignIn(AuthFactorHintValues.APPLE)
                        }
                        data-testid="simplified-apple-button"
                      >
                        <img
                          src={appleLogo}
                          alt=""
                          className={styles.socialIcon}
                        />
                        <span className={styles.socialLabel}>
                          {formatMessage(messages.appleLabel)}
                        </span>
                      </button>
                    )}
                    {enableGithub && (
                      <button
                        type="button"
                        className={styles.socialTile}
                        onClick={() =>
                          handleSocialSignIn(AuthFactorHintValues.GITHUB)
                        }
                        data-testid="simplified-github-button"
                      >
                        <img
                          src={githubLogo}
                          alt=""
                          className={styles.socialIcon}
                        />
                        <span className={styles.socialLabel}>
                          {formatMessage(messages.githubLabel)}
                        </span>
                      </button>
                    )}
                    {enableAmazon && (
                      <button
                        type="button"
                        className={styles.socialTile}
                        onClick={() =>
                          handleSocialSignIn(AuthFactorHintValues.AMAZON)
                        }
                        data-testid="simplified-amazon-button"
                      >
                        <img
                          src={amazonLogo}
                          alt=""
                          className={styles.socialIcon}
                        />
                        <span className={styles.socialLabel}>
                          {formatMessage(messages.amazonLabel)}
                        </span>
                      </button>
                    )}
                  </div>
                )}

                {/* Legal text */}
                <p className={styles.legalText}>
                  {formatMessage(messages.legalText, {
                    customerAgreement: (
                      <a
                        href="https://aws.amazon.com/agreement/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatMessage(messages.customerAgreement)}
                      </a>
                    ),
                    serviceTerms: (
                      <a
                        href="https://aws.amazon.com/service-terms/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatMessage(messages.serviceTerms)}
                      </a>
                    ),
                    privacyNotice: (
                      <a
                        href="https://aws.amazon.com/privacy/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatMessage(messages.privacyNotice)}
                      </a>
                    ),
                  })}
                </p>

                {/* Help section */}
                {helpExpanded && (
                  <div className={styles.helpSection}>
                    <div className={styles.helpDivider} />
                    <p className={styles.helpToggleLabel}>
                      {formatMessage(messages.getHelpSigningIn)}:
                    </p>
                    <div className={styles.helpTiles}>
                      <button
                        type="button"
                        className={styles.helpTile}
                        onClick={() => {
                          metricsPublisher.current.publishCounterMonitor(
                            'SwitchToOldUI',
                            1
                          );
                          rumService.recordEvent(
                            'SimplifiedSignInSwitchToOldUI',
                            {
                              url: window.location.href,
                              userAgent: navigator.userAgent,
                              path: `${window.location.pathname}${window.location.search}`,
                            }
                          );
                          const params = new URLSearchParams(
                            window.location.search
                          );
                          window.location.href = `${
                            AppURL.LoginClassic
                          }?${params.toString()}`;
                        }}
                        data-testid="switch-old-ui"
                      >
                        <img
                          src={keyIcon}
                          alt=""
                          className={styles.helpTileIcon}
                        />
                        <span className={styles.helpTileLabel}>
                          {formatMessage(messages.switchToOldUi)}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={styles.helpTile}
                        onClick={() => {
                          window.location.href = '/help';
                        }}
                        data-testid="faq-link"
                      >
                        <img
                          src={infoIcon}
                          alt=""
                          className={styles.helpTileIcon}
                        />
                        <span className={styles.helpTileLabel}>
                          {formatMessage(messages.faq)}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Help link - right aligned, hides when expanded */}
              {!helpExpanded && (
                <div className={styles.helpLink}>
                  <button
                    type="button"
                    onClick={() => setHelpExpanded(true)}
                    data-testid="help-toggle"
                  >
                    {formatMessage(messages.getHelpSigningIn)}
                  </button>
                </div>
              )}
            </div>
          </ContentLayout>
        </Main>
      </div>
      <Footer enableCookieComponent={enableCookieComponent} />
    </div>
  );
}

export default SimplifiedSignIn;
